import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface User {
  _id: string;
  username: string;
  tier: 'free' | 'pro';
  createdAt: string;
  isAdmin: boolean;
}

const AdminUserSelector: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const { isAdmin, impersonate, userId: currentUserId } = useAuth();

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    loadUsers();
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const userData = await api.getUsers();
      setUsers(userData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (userId: string) => {
    try {
      setImpersonating(userId);
      await impersonate(userId);
      window.location.reload();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to impersonate user');
      setImpersonating(null);
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">User Management (Admin)</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4" style={{ borderTop: '10px solid #dc3545' }}>
      <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">User Management (Admin Only)</h5>
        {/* Link rather than <Button as={Link}> — react-bootstrap's `as` prop doesn't
            type-check against react-router's Link here; the btn classes render identically. */}
        <Link to="/admin/platform" className="btn btn-outline-light btn-sm">
          Platform Overview
        </Link>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        <Alert variant="info">
          <strong>Impersonation Mode:</strong> Click "View As" to see the app as any user for debugging purposes.
          All actions will be logged for audit.
        </Alert>

        <Table responsive striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Username</th>
              <th>Tier</th>
              <th>Role</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user._id}>
                <td>
                  {user.username}
                  {user._id === currentUserId && <Badge bg="secondary" className="ms-2">You</Badge>}
                </td>
                <td>
                  <Badge bg={user.tier === 'pro' ? 'success' : 'secondary'}>
                    {user.tier}
                  </Badge>
                </td>
                <td>
                  {user.isAdmin ? (
                    <Badge bg="danger">Admin</Badge>
                  ) : (
                    <Badge bg="light" text="dark">User</Badge>
                  )}
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td>
                  {user._id !== currentUserId ? (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleImpersonate(user._id)}
                      disabled={impersonating === user._id}
                    >
                      {impersonating === user._id ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Switching...
                        </>
                      ) : (
                        'View As'
                      )}
                    </Button>
                  ) : (
                    <span className="text-muted">Current session</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default AdminUserSelector;
